import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import {
  Box,
  Checkbox,
  FieldSet,
  Label,
  LabeledControl,
  Legend,
  Paragraph,
  Toast,
  useToast,
} from '@zillow/constellation-10';

import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyEditWebApiHandler_hasAirConditioningUtilityIncluded,
  useRentalManagerUserPropertyEditWebApiHandler_hasCableTvUtilityIncluded,
  useRentalManagerUserPropertyEditWebApiHandler_hasElectricityUtilityIncluded,
  useRentalManagerUserPropertyEditWebApiHandler_hasGarbageUtilityIncluded,
  useRentalManagerUserPropertyEditWebApiHandler_hasGasUtilityIncluded,
  useRentalManagerUserPropertyEditWebApiHandler_hasHeatingUtilityIncluded,
  useRentalManagerUserPropertyEditWebApiHandler_hasHotWaterUtilityIncluded,
  useRentalManagerUserPropertyEditWebApiHandler_hasInternetUtilityIncluded,
  useRentalManagerUserPropertyEditWebApiHandler_hasSewageUtilityIncluded,
  useRentalManagerUserPropertyEditWebApiHandler_hasWaterUtilityIncluded,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../utils';

import { DEFAULT_ERROR_MESSAGE } from '../../../../constants/ErrorMessageConstants';
import { UTILITIES_KEYS, UTILITIES_METADATA, type UtilityKey } from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<UtilityKey, boolean>;

const UtilitiesAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const utilitiesIncluded = listingDetails?.buildingAttributes?.utilitiesIncluded;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasAirConditioning: utilitiesIncluded?.hasAirConditioning ?? false,
    hasCableTv: utilitiesIncluded?.hasCableTv ?? false,
    hasElectricity: utilitiesIncluded?.hasElectricity ?? false,
    hasGarbage: utilitiesIncluded?.hasGarbage ?? false,
    hasGas: utilitiesIncluded?.hasGas ?? false,
    hasHeating: utilitiesIncluded?.hasHeating ?? false,
    hasHotWater: utilitiesIncluded?.hasHotWater ?? false,
    hasInternet: utilitiesIncluded?.hasInternet ?? false,
    hasSewage: utilitiesIncluded?.hasSewage ?? false,
    hasWater: utilitiesIncluded?.hasWater ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Utilities updated',
  });

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updateAirConditioning } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasAirConditioningUtilityIncluded,
    skipToast: true,
    meta: { context: { api: 'hasAirConditioningUtilityIncluded', details: { listingAlias } } },
  });

  const { mutateAsync: updateCableTv } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasCableTvUtilityIncluded,
    skipToast: true,
    meta: { context: { api: 'hasCableTvUtilityIncluded', details: { listingAlias } } },
  });

  const { mutateAsync: updateElectricity } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasElectricityUtilityIncluded,
    skipToast: true,
    meta: { context: { api: 'hasElectricityUtilityIncluded', details: { listingAlias } } },
  });

  const { mutateAsync: updateGarbage } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasGarbageUtilityIncluded,
    skipToast: true,
    meta: { context: { api: 'hasGarbageUtilityIncluded', details: { listingAlias } } },
  });

  const { mutateAsync: updateGas } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasGasUtilityIncluded,
    skipToast: true,
    meta: { context: { api: 'hasGasUtilityIncluded', details: { listingAlias } } },
  });

  const { mutateAsync: updateHeating } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasHeatingUtilityIncluded,
    skipToast: true,
    meta: { context: { api: 'hasHeatingUtilityIncluded', details: { listingAlias } } },
  });

  const { mutateAsync: updateHotWater } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasHotWaterUtilityIncluded,
    skipToast: true,
    meta: { context: { api: 'hasHotWaterUtilityIncluded', details: { listingAlias } } },
  });

  const { mutateAsync: updateInternet } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasInternetUtilityIncluded,
    skipToast: true,
    meta: { context: { api: 'hasInternetUtilityIncluded', details: { listingAlias } } },
  });

  const { mutateAsync: updateSewage } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasSewageUtilityIncluded,
    skipToast: true,
    meta: { context: { api: 'hasSewageUtilityIncluded', details: { listingAlias } } },
  });

  const { mutateAsync: updateWater } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasWaterUtilityIncluded,
    skipToast: true,
    meta: { context: { api: 'hasWaterUtilityIncluded', details: { listingAlias } } },
  });

  const mutationMap: Record<keyof LocalState, typeof updateAirConditioning> = {
    hasAirConditioning: updateAirConditioning,
    hasCableTv: updateCableTv,
    hasElectricity: updateElectricity,
    hasGarbage: updateGarbage,
    hasGas: updateGas,
    hasHeating: updateHeating,
    hasHotWater: updateHotWater,
    hasInternet: updateInternet,
    hasSewage: updateSewage,
    hasWater: updateWater,
  };

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as UtilityKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = UTILITIES_METADATA[fieldName];
      await mutationMap[fieldName]({
        getParams: {
          [metadata.apiKey]: checked,
          listingAlias,
        },
      });
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(err, DEFAULT_ERROR_MESSAGE);
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <Box data-testid="UtilitiesAmenities" css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
      <Paragraph textStyle="body-bold">Utilities</Paragraph>

      <FieldSet legend={<Legend>Included in rent</Legend>} disabled={isInputFormDisabled}>
        <Box css={{ display: 'flex', flexDirection: 'row', gap: '400', flexWrap: 'wrap' }}>
          <Box css={{ display: 'flex', flexDirection: 'column', flex: '1 0 280px' }}>
            {UTILITIES_KEYS.slice(0, Math.floor((UTILITIES_KEYS.length + 1) / 2)).map((fieldName) => {
              const metadata = UTILITIES_METADATA[fieldName];
              return (
                <LabeledControl
                  key={`utilitiesIncluded-${metadata.label}`}
                  label={<Label>{metadata.label}</Label>}
                  control={
                    <Checkbox
                      name={fieldName}
                      value={String(!localState[fieldName])}
                      checked={localState[fieldName]}
                      onChange={handleCheckboxChange}
                    />
                  }
                />
              );
            })}
          </Box>

          <Box css={{ display: 'flex', flexDirection: 'column', flex: '1 0 280px' }}>
            {UTILITIES_KEYS.slice(Math.floor((UTILITIES_KEYS.length + 1) / 2)).map((fieldName, index) => {
              const metadata = UTILITIES_METADATA[fieldName];
              return (
                <LabeledControl
                  key={`utilitiesIncluded-${Math.floor((UTILITIES_KEYS.length + 1) / 2) + index}`}
                  label={<Label>{metadata.label}</Label>}
                  control={
                    <Checkbox
                      name={fieldName}
                      value={String(!localState[fieldName])}
                      checked={localState[fieldName]}
                      onChange={handleCheckboxChange}
                    />
                  }
                />
              );
            })}
          </Box>
        </Box>
      </FieldSet>
    </Box>
  );
};

export default memo(UtilitiesAmenities);
